<template>
  <div class="change-password-page">
    <!-- Animated Background -->
    <div class="animated-bg">
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
      <div class="gradient-orb orb-3"></div>
      <div class="mesh-overlay"></div>
    </div>

    <!-- Change Password Container -->
    <div class="change-password-container">
      <div class="change-password-card">
        <!-- Header -->
        <div class="card-header">
          <div class="header-icon">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              <circle cx="12" cy="16" r="1" />
            </svg>
          </div>
          <h1>Alterar Senha</h1>
          <p class="header-subtitle">
            Por motivos de segurança, é necessário alterar sua senha para
            continuar.
          </p>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit" class="password-form">
          <!-- New Password -->
          <div class="input-group">
            <label for="newPassword">Nova Senha</label>
            <div class="input-wrapper">
              <span class="input-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </span>
              <input
                id="newPassword"
                v-model="newPassword"
                :type="showNewPassword ? 'text' : 'password'"
                placeholder="Digite sua nova senha"
                required
                autocomplete="new-password"
                minlength="8"
              />
              <button
                type="button"
                class="toggle-password"
                @click="showNewPassword = !showNewPassword"
                :title="showNewPassword ? 'Ocultar senha' : 'Mostrar senha'"
              >
                <svg
                  v-if="!showNewPassword"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <svg
                  v-else
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                  />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Confirm Password -->
          <div class="input-group">
            <label for="confirmPassword">Confirmar Nova Senha</label>
            <div class="input-wrapper">
              <span class="input-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <polyline points="9 12 12 15 16 10" />
                </svg>
              </span>
              <input
                id="confirmPassword"
                v-model="confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                placeholder="Confirme sua nova senha"
                required
                autocomplete="new-password"
                minlength="8"
              />
              <button
                type="button"
                class="toggle-password"
                @click="showConfirmPassword = !showConfirmPassword"
                :title="showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'"
              >
                <svg
                  v-if="!showConfirmPassword"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <svg
                  v-else
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                  />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Password Requirements -->
          <div class="password-requirements">
            <p class="requirements-title">Requisitos da senha:</p>
            <ul class="requirements-list">
              <li :class="{ valid: hasMinLength }">
                <span class="check-icon">{{ hasMinLength ? "✓" : "○" }}</span>
                Mínimo de 8 caracteres
              </li>
              <li :class="{ valid: hasUppercase }">
                <span class="check-icon">{{ hasUppercase ? "✓" : "○" }}</span>
                Uma letra maiúscula
              </li>
              <li :class="{ valid: hasLowercase }">
                <span class="check-icon">{{ hasLowercase ? "✓" : "○" }}</span>
                Uma letra minúscula
              </li>
              <li :class="{ valid: hasNumber }">
                <span class="check-icon">{{ hasNumber ? "✓" : "○" }}</span>
                Um número
              </li>
              <li :class="{ valid: hasSpecialChar }">
                <span class="check-icon">{{ hasSpecialChar ? "✓" : "○" }}</span>
                Um caractere especial (@, #, $, etc.)
              </li>
              <li :class="{ valid: passwordsMatch }">
                <span class="check-icon">{{ passwordsMatch ? "✓" : "○" }}</span>
                Senhas coincidem
              </li>
            </ul>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            class="submit-btn"
            :disabled="loading || !isFormValid"
          >
            <span v-if="loading" class="loading-spinner"></span>
            <span>{{ loading ? "Alterando..." : "Alterar Senha" }}</span>
            <svg
              v-if="!loading"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="9 11 12 14 22 4" />
              <path
                d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
              />
            </svg>
          </button>

          <!-- Error Message -->
          <p v-if="error" class="error-msg">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {{ error }}
          </p>

          <!-- Success Message -->
          <p v-if="success" class="success-msg">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            {{ success }}
          </p>
        </form>

        <!-- Footer -->
        <footer class="card-footer">
          <p class="security-note">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Sua senha será criptografada e armazenada com segurança.
          </p>
          <button
            v-if="error"
            type="button"
            class="skip-link"
            @click="forceSkipPasswordChange"
          >
            Já alterou a senha? Clique aqui para continuar
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { changePassword } from "@/services/authService";
import { logger } from "@/utils/logger";

const router = useRouter();
const auth = useAuthStore();

// Form state
const newPassword = ref("");
const confirmPassword = ref("");
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);
const loading = ref(false);
const error = ref(null);
const success = ref(null);

// Password validation
const hasMinLength = computed(() => newPassword.value.length >= 8);
const hasUppercase = computed(() => /[A-Z]/.test(newPassword.value));
const hasLowercase = computed(() => /[a-z]/.test(newPassword.value));
const hasNumber = computed(() => /[0-9]/.test(newPassword.value));
const hasSpecialChar = computed(() =>
  /[!@#$%^&*(),.?":{}|<>]/.test(newPassword.value),
);
const passwordsMatch = computed(
  () =>
    newPassword.value.length > 0 && newPassword.value === confirmPassword.value,
);

const isFormValid = computed(
  () =>
    hasMinLength.value &&
    hasUppercase.value &&
    hasLowercase.value &&
    hasNumber.value &&
    hasSpecialChar.value &&
    passwordsMatch.value,
);

// Verifica no mount se realmente precisa trocar senha
onMounted(() => {
  // Se não precisa trocar senha e chegou aqui por engano, redireciona
  if (!auth.needsPasswordChange) {
    logger.auth("Não precisa trocar senha, redirecionando para home");
    router.replace("/home");
  }
});

async function handleSubmit() {
  if (!isFormValid.value) return;

  error.value = null;
  success.value = null;
  loading.value = true;

  try {
    const result = await changePassword(newPassword.value);

    // Limpa a flag no store
    auth.clearPasswordChangeRequired();

    // Mensagem diferenciada se a senha já tinha sido alterada
    if (result.alreadyChanged) {
      success.value =
        "Sua senha já foi alterada anteriormente. Redirecionando...";
    } else {
      success.value = "Senha alterada com sucesso! Redirecionando...";
    }

    // Aguarda 2 segundos e redireciona
    setTimeout(() => {
      router.replace("/home");
    }, 2000);
  } catch (err) {
    logger.error("Erro ao alterar senha:", err);
    error.value = err.message || "Erro ao alterar a senha. Tente novamente.";
  } finally {
    loading.value = false;
  }
}

/**
 * Força pular a tela de troca de senha
 * Usado quando o usuário já trocou a senha mas ficou preso no loop
 */
function forceSkipPasswordChange() {
  logger.warn("Forçando skip do password change");
  auth.clearPasswordChangeRequired();
  localStorage.removeItem("password_change_required");
  router.replace("/home");
}
</script>

<style scoped>
/* ========================================
   CHANGE PASSWORD PAGE - Glass Morphism Design
   ======================================== */

.change-password-page {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Inter", "Segoe UI", system-ui, sans-serif;
}

/* Animated Background - Same as Login */
.animated-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #6366f1 100%);
  overflow: hidden;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.6;
  animation: float 20s ease-in-out infinite;
}

.orb-1 {
  width: 600px;
  height: 600px;
  background: linear-gradient(135deg, #22d3ee, #06b6d4);
  top: -200px;
  left: -100px;
  animation-delay: 0s;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: linear-gradient(135deg, #8b5cf6, #a78bfa);
  bottom: -150px;
  right: -100px;
  animation-delay: -7s;
}

.orb-3 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #f472b6, #ec4899);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: -14s;
}

@keyframes float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  25% {
    transform: translate(50px, -50px) scale(1.1);
  }
  50% {
    transform: translate(-30px, 30px) scale(0.95);
  }
  75% {
    transform: translate(40px, 40px) scale(1.05);
  }
}

.mesh-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: radial-gradient(
      at 40% 20%,
      rgba(99, 102, 241, 0.15) 0px,
      transparent 50%
    ),
    radial-gradient(at 80% 0%, rgba(6, 182, 212, 0.15) 0px, transparent 50%),
    radial-gradient(at 0% 50%, rgba(139, 92, 246, 0.15) 0px, transparent 50%),
    radial-gradient(at 80% 50%, rgba(236, 72, 153, 0.1) 0px, transparent 50%),
    radial-gradient(at 0% 100%, rgba(34, 211, 238, 0.1) 0px, transparent 50%);
}

/* Container */
.change-password-container {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 480px;
  padding: 20px;
}

/* Card */
.change-password-card {
  background: var(--bg-card);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.1);
}

/* Header */
.card-header {
  text-align: center;
  margin-bottom: 32px;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  color: white;
  margin-bottom: 20px;
}

.card-header h1 {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.header-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

/* Form */
.password-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.input-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 14px;
  color: #9ca3af;
  display: flex;
  align-items: center;
  z-index: 1;
}

.input-wrapper input {
  width: 100%;
  padding: 14px 48px;
  font-size: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  background: #f9fafb;
  transition: all 0.2s ease;
  color: #1f2937;
}

.input-wrapper input:focus {
  outline: none;
  border-color: #667eea;
  background: white;
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
}

.toggle-password {
  position: absolute;
  right: 14px;
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  transition: color 0.2s;
}

.toggle-password:hover {
  color: #667eea;
}

/* Password Requirements */
.password-requirements {
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e2e8f0;
}

.requirements-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
  margin: 0 0 12px 0;
}

.requirements-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.requirements-list li {
  font-size: 0.8rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.2s;
}

.requirements-list li.valid {
  color: #10b981;
}

.check-icon {
  font-size: 0.75rem;
  width: 14px;
  text-align: center;
}

/* Submit Button */
.submit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 24px;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 8px;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px -10px rgba(102, 126, 234, 0.5);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Messages */
.error-msg {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #dc2626;
  font-size: 0.875rem;
  margin: 0;
}

.success-msg {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  color: #16a34a;
  font-size: 0.875rem;
  margin: 0;
}

/* Footer */
.card-footer {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.security-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0;
}

.security-note svg {
  color: #10b981;
}

.skip-link {
  background: none;
  border: none;
  color: var(--accent-primary, #6366f1);
  font-size: 0.85rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 8px;
  transition: color 0.2s;
}

.skip-link:hover {
  color: var(--accent-primary-dark, #4f46e5);
}

/* Responsive */
@media (max-width: 480px) {
  .change-password-card {
    padding: 24px;
  }

  .header-icon {
    width: 64px;
    height: 64px;
  }

  .header-icon svg {
    width: 32px;
    height: 32px;
  }

  .card-header h1 {
    font-size: 1.5rem;
  }

  .requirements-list {
    grid-template-columns: 1fr;
  }
}
</style>
